/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018, 2019
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
import { types, getEnv, destroy } from 'mobx-state-tree';
import * as mobx from 'mobx';
import _ from 'underscore';
import RelationshipTooltipModel from './RelationshipTooltipModel';
import RelationshipTargetModel from './RelationshipTargetModel';
import Config from './Config';
import RelationshipInfoTooltipModel from './RelationshipInfoTooltipModel';
import { generateUniqueId } from 'core/utils';
import StringResources from 'nls/StringResources';
import Logger from 'util/Logger';
import { fromPromise, FULFILLED, PENDING, REJECTED } from 'mobx-utils';


function uniqueTargetIdIsInDataSource(uniqueId, dataSourceId) {
	return uniqueId.startsWith(`${dataSourceId}.`);
}

function generateUniqueTargetId(dataSourceId, targetId) {
	return `${dataSourceId}.${targetId}`;
}

const DEFAULT_NODES_LIMIT = 10;


export default types
	.model('RelationshipVisViewModel', {
		tooltip: RelationshipTooltipModel,
		targetIds: types.optional(types.array(types.frozen()), []),	/** current selected targeted column ID in the whole model*/
		dataSourceId: types.maybeNull( types.string ), /** dataSource unique ID */
		selectedFields: types.optional(types.array(types.frozen()), []),
		contextMenuField: types.optional(types.frozen())
	})
	.volatile(() => ({
		isFilterStrengthFeatureEnabled: false,
		targetInfo: [],//TODO Remove this when the StartingPoints APIs are in place
		messagePayload: null,	/** message object that stores information at different levels, such as warngin, error, info, etc. */
		nodesLimit: DEFAULT_NODES_LIMIT,	/** max number of nodes */
		showConcepts: Config.default_show_concepts, /** when true, each node is layed out as single field group, when false, nodes will be grouped, such as concepts group */
		visLoader: null, /** a VisLoader object loads the visualization bunbles, sets the data and renders the visualization. By design, it could be any interfaced visLoader */
		widget: null /** an object to contain an abstraction of the current widget */,
		conceptsViewEnabled: false /** Set to true to enable the concepts view, False, disabled*/,
		datasourcesStatus: {},
		contextMenuElement: null,
		targetModels: {},
		infoTooltipModel: null,
		filterCount: null,
		maxRequestsReached: false,
		exploreViewActivated: true,
		exploreViewId: null,
		logger: null,
		data: null
	}))
	.views((self) => ({
		/**
		 * @return {String} return the first target Id (Currently explore actually supports only one target ID)
		 */
		getCurrentTargetId() {
			if(self.targetIds && self.targetIds.length){
				return self.targetIds[0];
			}
			return null;
		},

		getTargetIds(){
			return self.targetIds;
		},

		isConceptsViewEnabled(){
			return self.conceptsViewEnabled;
		},

		isDatasourceErrorHandledOnce(dataSourceId){
			return self.datasourcesStatus[dataSourceId];
		},
		/**
		 * @return {object} - data promise
		 */
		whenRelationshipDataReady() {
			const targetModel = self.getTargetModel();
			const data = targetModel && targetModel.data;
			if (data) {
				return data;
			}
			return Promise.resolve(null);
		},
		/**
		 * @param {object} options
		 *                 options.datasourceId, model ref id for datasource options.targetId, unique ID for targetId
		 * @return {object} - the RelationshipTargetModel currently in use
		 */
		getTargetModel(options = {}) {
			const currentTargetId = options.targetId || self.getCurrentTargetId();
			if(currentTargetId){
				return self.targetModels[generateUniqueTargetId(options.dataSourceId || self.dataSourceId, currentTargetId)];
			}
			return null;
		},

		getIsFilterStrengthFeatureEnabled() {
			return true;
		},

		isNewStartingPointsFeatureEnabled() {
			return !getEnv(self).isFeatureEnabled('newStartingPoints', 'false');
		},
		isFilterStartingPointsEnabled() {
			const env = getEnv(self);
			return env && env.isFeatureEnabled && env.isFeatureEnabled('filterStartingPoints');
		},
		isRemoveFieldFeatureEnabled() {
			return getEnv(self).isFeatureEnabled('addRemoveField');
		},

		isAddFieldFeatureEnabled() {
			return getEnv(self).isFeatureEnabled('addRemoveField');
		},

		isEditScopeFeatureEnabled() {
			return !getEnv(self).isFeatureEnabled('editScope', 'false');
		},

		isShowRelationshipsFeatureEnabled() {
			return getEnv(self).isFeatureEnabled('showRelationships');
		},

		//When ON, we add timeout queryspec to the request payload
		isTimeoutFeatureEnabled() {
			return getEnv(self).isFeatureEnabled('relationshipsRequestTimeout');
		},

		//When ON, the underneath promise will call be aborted when it times out
		allowLongRunningRequest(){
			return !getEnv(self).isFeatureEnabled('allowLongRunningRelationshipRequest', false);
		},

		//When ON, we only allow N concurrent requests (Number N is defined in config)
		limitConcurrentRequests(){
			return !getEnv(self).isFeatureEnabled('limitConcurrentRequests', false);
		},

		isOverrideListFeatureEnabled() {
			return getEnv(self).isFeatureEnabled('relationshipDiagramOverrideList');
		},

		getInfoTooltipModel() {
			return self.infoTooltipModel;
		},

		getFilterCount() {
			return self.filterCount;
		},

		/**
		 * Check if the given target has the fulfilled relationshipData
		 * @param {*} targetId
		 * @return {boolean}
		 */
		isTargetHasNonRejectedRelationshipData(targetId) {
			const targetModel = self.getTargetModel( { targetId });
			return !!(targetModel && targetModel.data && targetModel.data.state !== REJECTED);
		},

		getRunningRequestsTargetIds(dataSourceId = self.dataSourceId) {
			return Object.keys(self.targetModels)
				.filter(uniqueTargetId => uniqueTargetIdIsInDataSource(uniqueTargetId, dataSourceId))
				.filter(uniqueTargetId => self.targetModels[uniqueTargetId].data && self.targetModels[uniqueTargetId].data.state === PENDING)
				.map(uniqueTargetId => self.targetModels[uniqueTargetId].id);
		},

		/**
		 * Get running relationship request for given datasource, default to current datasource
		 * @param {string} [datasourceId=self.dataSourceId] datasourceId
		 */
		getNumOfRunningRequests(dataSourceId = self.dataSourceId) {
			const runningRequests = self.getRunningRequestsTargetIds(dataSourceId);
			return runningRequests ? runningRequests.length : 0;
		},

		/**
		 * if can fetch data for the target
		 * @param {string} targetId
		 * @param {string} action
		 * @return {object}
		 */
		canFetchData(targetId, action) {
			//if feature is off, then directly return true
			if(!self.limitConcurrentRequests()) {
				return {
					canFetchData: true
				};
			}
			if(action === 'changeTarget') {
				// if max requsts reached and the target does not has the non rejected data(means need to send a new request), then cannot set target
				if (!(self.getNumOfRunningRequests() < Config.max_number_of_concurrent_requests || self.isTargetHasNonRejectedRelationshipData(targetId))) {
					return {
						canFetchData: false,
						infoDialogContent: {
							title: StringResources.get('max_running_relationship_requests_reached_title', { max_number_of_concurrent_requests: Config.max_number_of_concurrent_requests }),
							body: StringResources.get('max_running_relationship_requests_reached_body')
						}
					};
				}
			} else if(action === 'editScope') {
				if (!(self.getNumOfRunningRequests() < Config.max_number_of_concurrent_requests || self.getRunningRequestsTargetIds().indexOf(targetId) != -1)) {
					return {
						canFetchData: false,
						infoDialogContent: {
							title: StringResources.get('max_running_relationship_requests_reached_title'),
							body: StringResources.get('max_running_relationship_requests_reached_body', { max_number_of_concurrent_requests: Config.max_number_of_concurrent_requests })
						}
					};
				}
			}

			return {
				canFetchData: true
			};
		}

	}))
	.actions((self) => {
		const disposeFunctions = [];

		return {
			afterCreate() {
				//There is only one relationship vis view model per perspective
				self.logger = new Logger('CA_EXPLORE_RELATIONSHIP_VISVIEW_MODEL');
				self.cancelRequestDelegator = ()=> true;
				self.uid = generateUniqueId('relationshipVis_');
				self.infoTooltipModel = RelationshipInfoTooltipModel.create({
					isOpen: false
				});
				/*When target column ID changes, go fetch the data for the new target id*/
				disposeFunctions.push(mobx.reaction(() => {
					if(self.targetIds.length) {
						return generateUniqueTargetId(self.dataSourceId, self.targetIds[0]);
					} else {
						//When empty fields of interest returned, `setTargetIds([])` will be called,
						//but the `targetIds` is [] initially, so in order to react to this, so that
						//fetchData() can be called to show the `No field of interest` message, we
						//directly return `self.targetIds` when it is an empty array, cause [] !== []
						//TODO: A better way would be seting `targetIds` to null on initialization.
						return self.targetIds;
					}
				}, () =>{
					if(self.targetIds.length>0){
						const uniqueTargetId = generateUniqueTargetId(self.dataSourceId, self.targetIds[0]);
						if (!self.targetModels[uniqueTargetId] && self.getIsFilterStrengthFeatureEnabled()) {
							self.targetModels[uniqueTargetId] =  RelationshipTargetModel.create(
								undefined,
								{ exploreVM: getEnv(self).exploreVM,
									relationshipVM: self,
									id: self.targetIds[0],
									label: self.targetInfo[0].label }
							);
						}
					}
					if (self.getIsFilterStrengthFeatureEnabled()) {
						self.fetchData({
							setTarget: true, // this flag is to indicate current action is switiching to different target
							transformOptions: {
								filterStrength: null // just to trigger the transformation, the real min, max will be picked up in transformation code
							}
						});
					} else {
						self.fetchData();
					}
				}));

				disposeFunctions.push(mobx.reaction(()=>{
					return self.dataSourceId;
				}, (dataSourceId)=>{
					if(dataSourceId){
						self.setDatasourceHandledStatus(dataSourceId, false);
						if(self.isFilterStartingPointsEnabled()) {
							self._fetchFilterCount(); // Fetch filter count on reaction to change in datasourceId
						}
					}
					if(!self.getIsFilterStrengthFeatureEnabled()) {
						self.reset();	//It is no longer appropriate to do a reset when changing data sources - using separate target models replaces this.
					}
				}));

				/*When nodes limit changes, go fetch the data for the new nodes limit*/
				disposeFunctions.push(mobx.reaction(() => {
					return self.nodesLimit;
				}, ()=>{
					self.fetchData();
				}));

				/*When showConcepts changes to true or false, go fetch the data */
				disposeFunctions.push(mobx.reaction(() => {
					return self.showConcepts;
				}, ()=>{
					self.fetchData();
				}));

				/*When fetched data changes, update visLoader */
				disposeFunctions.push(mobx.reaction(
					() => {
						const targetModel = self.getTargetModel();
						return {
							hasWidget: self.widget? true : false,
							targetModel: targetModel,
							state: targetModel && targetModel.data? targetModel.data.state : null
						};
					},
					() => {
						const targetModel = self.getTargetModel();
						if(self.widget && self.widget.setData) {
							const state =  targetModel && targetModel.data && targetModel.data.state;
							if(state === FULFILLED) {
								//Before diagram change, dismiss the context menu
								self.setContextMenu({ element: null, field: null });
								self.widget.setData(targetModel.data.value);
							}
						}
					}
				));

				disposeFunctions.push(
					mobx.autorun(() => {
						getEnv(self).dashboardApi.on('pagecontext:filterContextUpdated', () => {
							self._fetchFilterCount();
						});
					})
				);
			},
			beforeDestroy() {
				disposeFunctions.forEach(dispose => dispose());
				if(self.visLoader ) {
					self.visLoader.unload();
				}
				if (self.infoTooltipModel) {
					destroy(self.infoTooltipModel);
				}
				const targetModels = _.values(self.targetModels);
				if (targetModels.length) {
					targetModels.forEach(model => destroy(model));
					self.targetModels = {};
				}
			},

			destroyTargetModel(dataSourceId, targetId) {
				const uniqueTargetId = generateUniqueTargetId(dataSourceId,targetId);
				const targetModel = self.targetModels[uniqueTargetId];
				if(targetModel) {
					destroy(targetModel);
					delete self.targetModels[uniqueTargetId];
				}
			},

			setFilterCount(count) {
				self.filterCount = count;
			},

			_fetchFilterCount() {
				const selector = {
					scope: 'starting points',
					origin: 'filter',
					sourceId: self.dataSourceId,
					_strictMatch: true
				};
				const filterCount = getEnv(self).dashboardApi.getFeature('GlobalFilters').getFilterList(selector).length;
				self.setFilterCount(filterCount);
			},

			setWidget(widget) {
				self.widget = widget;
			},
			/**
			 * @param e DOM node widget is being loaded into
			 */
			setElement(e) {
				return self.visLoader
					.load(e)
					.then(o => {
						const { setData } = o;
						self.setWidget({ setData });	//The only part of the widget abstraction we need is the function to set its data
						return o;
					});
			},

			/**
			 * @param {string[]} targetIds  set targetIds to model
			 * @param {[string[]]} target information such as metadata info, default value is {label: targetId}
			 */
			setTargetIds(targetIds, targetInfo = [{ label: targetIds[0] }]){
				self.targetIds = targetIds;
				self.targetInfo = targetInfo;
			},

			resetTargetModels() {
				Object.keys(self.targetModels)
					.filter(id => uniqueTargetIdIsInDataSource(id, self.dataSourceId))
					.forEach(id => self.targetModels[id].reset());
			},

			/**
			 *@param {String} targetIds  set targetIds to model
			 **/
			setDataSourceId(dataSourceId){
				self.dataSourceId = dataSourceId;
			},

			/**
			 *@param {Object} wrapper pass the recommenderWrapper to relationship model
			 **/
			setSmartsRecommenderWrapper(wrapper){
				self.smartsRecommenderWrapper = wrapper;
			},


			setShowConcepts(value){
				self.showConcepts = value;
			},

			setVisLoader(loader){
				self.visLoader = loader;
			},

			setNodesLimit(limit){
				self.nodesLimit = limit;
			},

			setIsFilterStrengthFeatureEnabled(value){
				self.isFilterStrengthFeatureEnabled = value;
			},

			/**
			 * Set a flag for each datasource ID to indicate if the datasource has been handled
			 * Currently it is used for error handling cases only
			 */
			setDatasourceHandledStatus(dataSourceId, handled){
				self.datasourcesStatus[dataSourceId] = handled;
			},

			/**
			 * Set the explore content view activation status
			 * @param {boolean} value True means the content view is visible
			 */
			setExploreViewActivated(value){
				self.exploreViewActivated = value;
			},

			/**
			 * Set the explore content view unqiue Id
			 * @param {string} value
			 */
			setExploreViewId(value){
				self.exploreViewId = value;
			},

			/**
			 * Set glassContext
			 * //TODO: When there are API to return glassContext, this shouldn't be needed
			 * @param {object} context
			 */
			setGlassContext(context){
				self.glassContext = context;
			},

			/**
			 * Main function that 'fetches' raw data for the relationship vis view model
			 * This raw data will be processed and parsed into the data structure that is accepted
			 * by the relationship widget.
			 *
			 * Depending on the scenario, underneapth, the raw data might be returned from an external service
			 * via HTTP request, or from a browser end data cache.
			 *
			 * @param {object} fetchDataContext
			 * 	transformationOptions: {
			 * 		'addedField': <column id>,  //TODO Support multiple fields
			 * 		'deletedField': <column id>
			 * 	}
			 */
			fetchData(fetchDataContext) {
				if(!self.targetIds || !self.targetIds.length ) {
					self.logger.warn('CA_EXPLORE_TARGET_ID_NOT_SET');
				} else if(self.targetIds.length === 1) {
					let nodesLimit;
					if (self.getIsFilterStrengthFeatureEnabled()) {
						nodesLimit = Config.max_nodes_to_fetch;
					} else {
						nodesLimit = self.nodesLimit;
					}
					self._sendRequest(nodesLimit, fetchDataContext);
				}
			},

			_sendRequest(nodesLimit, fetchDataContext){
				const targetModel = self.getTargetModel();
				const fetchDataAnyway = self._isFetchingScopedData(fetchDataContext);
				/**
				 we need to send new request when
				 1. we need to fetch data anyway
				 2. or current data is not in pending state
				 */
				if(targetModel && (fetchDataAnyway || !targetModel.data || targetModel.data.state !== PENDING)){
					if(!fetchDataAnyway) {
						fetchDataContext = targetModel.modifyFetchDataContextIfNeeded(fetchDataContext);
					}
					//When the request cancel delegator is called, it will cause a 'request cancel'
					//please find SmartsRecommenderWrapper for more details
					const cancelPromise = new Promise( (resolve, reject) => {
						self.cancelRequestDelegator = reject;
					});
					//build query params for fecthing data
					const queryParams = {
						dataSourceId: self.dataSourceId,
						targetId: self.getCurrentTargetId(),//only allow one target ID currently
						maxInfluencerFields: nodesLimit,
						showConcepts: self.showConcepts,
						context: {
							relationshipVM: self,
							transformOptions: fetchDataContext && fetchDataContext.transformOptions || {}
						}
					};
					targetModel.resetRequestTimer();
					targetModel.startRequestTimer();

					// if there is a currently running request for this target, we abandon it
					if(targetModel.data && targetModel.data.state === PENDING) {
						targetModel.abandonRequest();
					}
					const getRelStrengthPromise = self.smartsRecommenderWrapper.getRelationshipStrength(
						queryParams,
						null,
						cancelPromise
					);
					targetModel.setData(
						fromPromise(
							getRelStrengthPromise
						)
					);
				}
			},

			_isFetchingScopedData(fetchDataContext) {
				return !!(fetchDataContext && fetchDataContext.transformOptions && fetchDataContext.transformOptions.scopedFields);
			},

			/**
			 * @param {object[]} items - fields to add to the list of deleted fields, so that they're suppressed from the relationship diagram
			 */
			deleteFields(items) {
				self.getTargetModel().deleteFields(items.map(({ itemId }) => itemId));
				self.setSelectedFields([]);
			},

			/**
			 * @param {object[]} items
			 * @return {boolean} indicates whether it is possible to delete the specified fields
			 */
			canDeleteFields(items) {
				if(!self.isRemoveFieldFeatureEnabled()) {
					return false;
				}
				const itemIds = items.map(({ itemId }) => itemId);
				return _.intersection(self.targetIds || [], itemIds).length === 0;
			},

			cleanup() {
				if(self.widget && self.widget.setData){
					self.widget.setData(null);
					self.setWidget(null);
				}
			},

			reset() {
				self.setNodesLimit(DEFAULT_NODES_LIMIT);
				self.setShowConcepts(Config.default_show_concepts);
				if (self.getIsFilterStrengthFeatureEnabled()) {
					self.resetTargetModels();
				}

			},
			/**
			 * Set which fields have been selected by the user
			 * @param {object[]} selectedFields
			 */
			setSelectedFields(selectedFields) {
				self.selectedFields = selectedFields.slice();
			},

			/**
			 * Set information for the context menu
			 * @param {object} options
			 * @param {object} options.field - which field the context menu will perform operations on
			 * @param {Element} options.element - which element the context menu should appear next \to
			 */
			setContextMenu({ element, field }) {
				self.contextMenuElement = element;
				self.contextMenuField = field;
			},

			toggleInfoTooltip(triggerNode) {
				if (self.infoTooltipModel.isTooltipOpen()) {
					self.infoTooltipModel.closeTooltip();
				} else {
					self.infoTooltipModel.openTooltip(triggerNode);
				}
			},

			/**
			 * If can show the show relationships button on the context menu
			 */
			canShowRelationships() {
				return self.isShowRelationshipsFeatureEnabled();
			}
		};
	});



// WEBPACK FOOTER //
// ./src/js/components/startingPoints/relationshipGraph/RelationshipVisViewModel.js